import React, { useCallback, useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import CommunicationComposer, { type RecipientGroup, type GroupDraftState, type CommunicationRecipient } from './CommunicationComposer';
import { communicationsApi, type CommunicationsRecipient } from '../../lib/api';
import { toast } from 'sonner';

export type MessageChannel = 'email' | 'sms';

export type MessageRecipient =
  | {
      id: string;
      type: 'person';
      wpUserId: number;
      subIndex?: number | null;
      name: string;
      email?: string | null;
      phone?: string | null;
    }
  | {
      id: string;
      type: 'trial_request';
      trialRequestId: number;
      name: string;
      email?: string | null;
      phone?: string | null;
    };

export type MessageWizardModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  standortId: number;
  title?: string;
  description?: string;

  recipients: MessageRecipient[];

  initialChannel?: MessageChannel;
  purpose?: 'transactional' | 'marketing';
  /** Zeigt die Wahl „Information / Werbung“; sonst gilt `purpose`. */
  zweckWaehlbar?: boolean;
  templateKeys?: Partial<Record<MessageChannel, string>>;
  vars?: Record<string, any>;

  initialDrafts?: {
    email?: { subjectTpl?: string; htmlTpl?: string; textTpl?: string };
    sms?: { smsTpl?: string };
  };
  /** Was tatsächlich rausging — damit der Aufrufer es in die Historie schreiben kann. */
  /** Vorgang, aus dem heraus geschrieben wird — landet in dessen Historie. */
  context?: { entityType: 'trial' | 'trial_request' | 'person' | 'enrollment'; entityId: number };
  onSent?: (versandt: {
    channels: Array<'email' | 'sms'>;
    subject?: string;
    text?: string;
  }) => void | Promise<void>;
};

function toApiRecipient(r: MessageRecipient): CommunicationsRecipient {
  if (r.type === 'person') {
    return { type: 'person', wpUserId: r.wpUserId, subIndex: r.subIndex ?? undefined };
  }
  return { type: 'trial_request', trialRequestId: r.trialRequestId };
}

export default function MessageWizardModal({
  open,
  onOpenChange,
  standortId,
  title = 'Nachricht senden',
  description,
  recipients,
  initialChannel = 'email',
  purpose: zweckVorgabe = 'transactional',
  zweckWaehlbar = false,
  templateKeys,
  vars,
  initialDrafts,
  context,
  onSent,
}: Readonly<MessageWizardModalProps>) {
  const [draftState, setDraftState] = useState<GroupDraftState | null>(null);
  const [isSending, setIsSending] = useState(false);
  // Massenmail (T-0178): Wer schreibt, sagt, ob es Werbung ist — dann gehen
  // nur Personen mit Werbe-Einwilligung raus.
  const [purpose, setPurpose] = useState<'transactional' | 'marketing'>(zweckVorgabe);

  // Build a single RecipientGroup from flat recipients
  const composerGroups: RecipientGroup[] = useMemo(() => {
    if (recipients.length === 0) return [];
    return [{
      key: 'default',
      label: 'Empfänger',
      recipients: recipients as CommunicationRecipient[],
      templateKeys: templateKeys
        ? { email: templateKeys.email, sms: templateKeys.sms }
        : undefined,
      vars,
      defaultSelectedChannels: { email: initialChannel === 'email', sms: initialChannel === 'sms' },
    }];
  }, [recipients, templateKeys, vars, initialChannel]);

  const initialGroupDrafts = useMemo(() => ({
    default: {
      email: {
        subjectTpl: initialDrafts?.email?.subjectTpl || '',
        htmlTpl: initialDrafts?.email?.htmlTpl || '',
        textTpl: initialDrafts?.email?.textTpl || '',
      },
      sms: {
        smsTpl: initialDrafts?.sms?.smsTpl || '',
      },
    },
  }), [initialDrafts?.email?.subjectTpl, initialDrafts?.email?.htmlTpl, initialDrafts?.email?.textTpl, initialDrafts?.sms?.smsTpl]);

  const handleDraftsChange = useCallback(
    (state: { groups: Record<string, GroupDraftState> }) => {
      const g = state.groups['default'];
      if (g) setDraftState(g);
    },
    [],
  );

  const canSend = useMemo(() => {
    if (!draftState) return false;
    const { selectedChannels, email, sms } = draftState;
    if (selectedChannels.email && email.subjectTpl.trim() && email.htmlTpl.trim()) return true;
    if (selectedChannels.sms && sms.smsTpl.trim()) return true;
    return false;
  }, [draftState]);

  const handleSend = async () => {
    if (!draftState) return;
    setIsSending(true);
    const apiRecipients = recipients.map(toApiRecipient);

    try {
      const promises: Promise<any>[] = [];

      if (draftState.selectedChannels.email) {
        promises.push(
          communicationsApi.send({
            channel: 'email',
            standortId,
            purpose,
            context,
            recipients: apiRecipients,
            draft: {
              subjectTpl: draftState.email.subjectTpl,
              htmlTpl: draftState.email.htmlTpl,
              textTpl: draftState.email.textTpl || undefined,
            },
          }),
        );
      }

      if (draftState.selectedChannels.sms) {
        promises.push(
          communicationsApi.send({
            channel: 'sms',
            standortId,
            purpose,
            context,
            recipients: apiRecipients,
            draft: { smsTpl: draftState.sms.smsTpl },
          }),
        );
      }

      const antworten = await Promise.all(promises);
      // Der Server antwortet auch bei gescheitertem Versand mit 200 und nennt den
      // Fehler je Empfänger. Bisher hieß es trotzdem „gesendet“ — eine SMS ohne
      // Anbieter ging so still verloren (T-0017).
      const fehler = antworten
        .flatMap((antwort) => (Array.isArray(antwort?.results) ? antwort.results : []))
        .filter((zeile: any) => zeile && zeile.ok === false)
        .map((zeile: any) => `${zeile.channel === 'sms' ? 'SMS' : 'E-Mail'} an ${zeile.to || 'Empfänger'}: ${zeile.error || 'nicht versendet'}`);
      const channels: Array<'email' | 'sms'> = [];
      if (draftState.selectedChannels.email) channels.push('email');
      if (draftState.selectedChannels.sms) channels.push('sms');
      await onSent?.({
        channels,
        subject: draftState.selectedChannels.email ? draftState.email.subjectTpl : undefined,
        text: draftState.selectedChannels.email
          ? (draftState.email.textTpl || draftState.email.htmlTpl)
          : draftState.sms.smsTpl,
      });
      if (fehler.length > 0) {
        toast.error(`Nicht alles wurde versendet – ${fehler.join('; ')}`);
        return;
      }
      toast.success('Nachricht wurde gesendet');
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Versand fehlgeschlagen');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      onPrimary={handleSend}
      primaryLabel="Senden"
      canPrimary={canSend && !isSending}
      isLoading={isSending}
    >
      <div className="py-4">
        {zweckWaehlbar && (
          <fieldset className="mb-4 rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">Art der Nachricht</legend>
            <div className="flex flex-wrap gap-4 text-sm">
              {(
                [
                  ['transactional', 'Information', 'z. B. Unterrichtsausfall, Termine — geht an alle'],
                  ['marketing', 'Werbung', 'Newsletter, Aktionen — nur an Personen mit Werbe-Einwilligung'],
                ] as const
              ).map(([wert, label, hinweis]) => (
                <label key={wert} className="flex cursor-pointer items-start gap-2">
                  <input
                    type="radio"
                    name="nachricht-art"
                    className="mt-1"
                    checked={purpose === wert}
                    onChange={() => setPurpose(wert)}
                  />
                  <span>
                    <span className="font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hinweis}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <CommunicationComposer
          active={open}
          standortId={standortId}
          purpose={purpose}
          groups={composerGroups}
          initialGroupDrafts={initialGroupDrafts}
          onDraftsChange={handleDraftsChange}
        />
      </div>
    </WizardShell>
  );
}
